import React, { useState, useEffect, useCallback } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useNotification } from "../../context/NotificationContext";
import { HomepageSectionSettingServices } from "../../services/admin/HomepageSectionSettingServices";
import { Save, Edit2, X, Loader2, Type } from "lucide-react";

// Minimal toolbar — bold, italic, bullet list, and clear.
// Keeps the admin from injecting complex/unsafe HTML.
const QUILL_MODULES = {
    toolbar: [
        ["bold", "italic"],
        [{ list: "bullet" }],
        ["clean"],
    ],
};
const QUILL_FORMATS = ["bold", "italic", "list", "bullet"];

export default function HomepageSectionSetting() {
    const { showNotification } = useNotification();
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);

    const [formData, setFormData] = useState({
        section_title: "",
        section_body: "",
    });
    const [originalData, setOriginalData] = useState({});

    const fetchSettings = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await HomepageSectionSettingServices.getSettings();
            if (response.status === "success") {
                setFormData(response.data);
                setOriginalData(response.data);
            }
        } catch {
            showNotification({
                type: "failed",
                title: "Load Error",
                message: "Failed to load homepage section settings.",
            });
        } finally {
            setIsLoading(false);
        }
    }, [showNotification]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const validate = () => {
        if (!formData.section_title.trim()) {
            showNotification({
                type: "error",
                title: "Validation Error",
                message: "Section title is required.",
            });
            return false;
        }
        // Strip all HTML tags to check for actual text content
        const strippedBody = formData.section_body.replace(/<[^>]*>/g, "").trim();
        if (!strippedBody) {
            showNotification({
                type: "error",
                title: "Validation Error",
                message: "Section body content is required.",
            });
            return false;
        }
        return true;
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSaving(true);
        try {
            // Normalize non-breaking spaces to standard spaces so text flows cleanly
            const cleanTitle = formData.section_title.trim();
            const cleanBody = formData.section_body
                .replace(/&nbsp;/g, " ")
                .replace(/\u00a0/g, " ");

            const response = await HomepageSectionSettingServices.updateSettings({
                section_title: cleanTitle,
                section_body: cleanBody,
            });
            if (response.status === "success") {
                showNotification({
                    type: "success",
                    title: "Settings Saved",
                    message: "Homepage section updated successfully.",
                });
                fetchSettings();
                setIsEditing(false);
            } else {
                showNotification({
                    type: "error",
                    title: "Error",
                    message: response.message || "Failed to update settings.",
                });
            }
        } catch (err) {
            showNotification({
                type: "error",
                title: "Error",
                message: err.message || "An unexpected error occurred.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData(originalData);
        setIsEditing(false);
    };

    const inputClasses =
        "w-full pl-12 pr-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-[#FAFAFA] dark:bg-[#222] text-sm focus:border-jazz-brown outline-none transition-colors disabled:opacity-75 disabled:cursor-not-allowed";
    const labelClasses =
        "block text-xs font-bold text-black dark:text-white uppercase tracking-widest mb-2";

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <Loader2 className="animate-spin text-jazz-brown" size={48} />
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-10 max-w-5xl mx-auto">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
                <div>
                    <h1
                        className="text-3xl lg:text-4xl font-normal text-black dark:text-white mb-2"
                        style={{ fontFamily: "Playfair Display, serif" }}
                    >
                        Homepage — "Legacy in Motion"
                    </h1>
                    <p className="text-black dark:text-white/50 text-sm font-serif">
                        Edit the section title and body text shown in the biography teaser on the
                        homepage. Images are managed separately and are not affected here.
                    </p>
                </div>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-2 px-6 py-2.5 bg-black text-white rounded-xl hover:bg-jazz-brown transition-colors text-xs font-bold tracking-widest uppercase shrink-0"
                    >
                        <Edit2 size={16} />
                        Edit Section
                    </button>
                )}
            </div>

            <div className="bg-white dark:bg-[#1A1A1A] p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                <form onSubmit={handleSave} className="space-y-8">

                    {/* Section Title */}
                    <div>
                        <label className={labelClasses}>
                            Section Title <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Type
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40"
                                size={18}
                            />
                            <input
                                type="text"
                                id="homepage-section-title"
                                value={formData.section_title}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        section_title: e.target.value,
                                    }))
                                }
                                disabled={!isEditing}
                                className={inputClasses}
                                placeholder="e.g. Legacy in Motion"
                            />
                        </div>
                    </div>

                    {/* Section Body — Rich Text */}
                    <div>
                        <label className={labelClasses}>
                            Section Body <span className="text-red-500">*</span>
                        </label>

                        {isEditing ? (
                            <div className="homepage-quill-wrapper rounded-xl overflow-hidden border border-black/10 dark:border-white/10">
                                <ReactQuill
                                    id="homepage-section-body"
                                    theme="snow"
                                    value={formData.section_body}
                                    onChange={(value) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            section_body: value,
                                        }))
                                    }
                                    modules={QUILL_MODULES}
                                    formats={QUILL_FORMATS}
                                    placeholder="Write the biography teaser text here..."
                                />
                            </div>
                        ) : (
                            /* View mode — render the stored HTML as a preview */
                            <div
                                className="jazz-journey-body w-full max-w-full overflow-hidden break-words text-black/80 dark:text-white/80 text-sm leading-relaxed p-4 bg-[#FAFAFA] dark:bg-[#222] rounded-xl border border-black/5 min-h-[80px]"
                                dangerouslySetInnerHTML={{
                                    __html:
                                        (formData.section_body || "")
                                            .replace(/&nbsp;/g, " ")
                                            .replace(/\u00a0/g, " ") ||
                                        "<p class='italic opacity-40'>No content set yet. Click Edit Section to add content.</p>",
                                }}
                            />
                        )}

                        <p className="text-[10px] text-black/40 dark:text-white/30 mt-2 uppercase tracking-widest">
                            Tip: Keep the body concise — this is a teaser, not the full biography.
                        </p>
                    </div>

                    {/* Action Buttons (edit mode only) */}
                    {isEditing && (
                        <div className="flex flex-col-reverse sm:flex-row justify-end gap-4 pt-6 mt-6 border-t border-black/5 dark:border-white/5">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="flex items-center justify-center gap-2 px-8 py-3 bg-gray-100 text-black rounded-xl hover:bg-gray-200 transition-colors text-xs font-bold tracking-widest uppercase w-full sm:w-auto"
                            >
                                <X size={16} />
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="flex items-center justify-center gap-2 px-8 py-3 bg-jazz-brown text-white rounded-xl hover:bg-black transition-colors text-xs font-bold tracking-widest uppercase disabled:opacity-50 disabled:cursor-not-allowed shadow-lg w-full sm:w-auto"
                            >
                                {isSaving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Update Section
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
